<template>
  <div class="sparkline_container" ref="chartRef">
    <apexchart v-if="visible && series" :type="chartType" height="100%" width="100%" ref="chart" :key="chartKey"
      :options="chartOptions" :series="series">
    </apexchart>
  </div>
</template>
<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted, ref, watch } from "vue";
import VueApexCharts from "vue3-apexcharts";

interface ChartDataPoint {
  x: number | Date;
  y: number;
}

export default defineComponent({
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    isPositive: Boolean,
    data: {
      type: Array as () => number[] | ChartDataPoint[],
      default: () => []
    },
    isLine: Boolean,
    color: String,
    delay: {
      type: Number,
      default: 0
    }
  },
  setup(props) {
    const chartColor = ref(
      props.color ? props.color : props.isPositive ? "#70E10E" : "#FF3B3B"
    );
    const chartType = ref<string>(props.isLine ? "line" : "area");
    const visible = ref(false);
    const series = ref<{ data: ChartDataPoint[] }[]>([]);
    const chart = ref<any>(null);
    const chartKey = ref(0);
    const chartRef = ref<HTMLElement | null>(null);
    let observer: IntersectionObserver | null = null;

    // Helper function to normalize data
    const normalizeData = async (data: any[]): Promise<ChartDataPoint[]> => {
      return new Promise((resolve) => {
        try {
          const normalized = data.map((item, index) => {
            if (typeof item === 'number') {
              return {
                x: new Date().getTime() + index,
                y: item
              };
            }
            if (item && typeof item.y !== 'undefined') {
              return item;
            }
            return { x: index, y: 0 };
          });
          resolve(normalized);
        } catch (error) {
          console.error('Error normalizing chart data:', error);
          resolve([]);
        }
      });
    };

    // Update chart series with proper error handling
    const updateChartSeries = async (data: any[]) => {
      try {
        const normalizedData = await normalizeData(data);

        return new Promise<void>((resolve) => {
          if (chart.value?.updateSeries) {
            chart.value.updateSeries(
              [{
                data: normalizedData
              }],
              false,
              true
            );
          }
          resolve();
        });
      } catch (error) {
        console.error('Error updating chart series:', error);
      }
    };

    // Initialize chart data
    const initializeChartData = async () => {
      const delay = props.delay * 500 || 0;

      await new Promise(resolve => setTimeout(resolve, delay));

      try {
        if (props.data?.length) {
          const normalized = await normalizeData(props.data);
          series.value = [{ data: normalized }];
        } else {
          series.value = [{
            data: Array.from({ length: 20 }, (_, i) => ({
              x: i,
              y: Math.floor(Math.random() * 10)
            }))
          }];
        }

        if (chart.value) {
          await updateChartSeries(series.value[0].data);
        }
      } catch (error) {
        console.error('Error initializing chart data:', error);
      }
    };

    watch(
      () => props.isPositive,
      (newVal) => {
        chartColor.value = props.color || (newVal ? "#70E10E" : "#FF3B3B");
        if (chart.value?.updateOptions) {
          chart.value.updateOptions({ colors: [chartColor.value] });
        }
      }
    );

    watch(
      () => props.data,
      async () => {
        if (!visible.value) return;
        await initializeChartData();
      },
      { deep: true }
    );

    onMounted(async () => {
      observer = new IntersectionObserver(
        async ([entry]) => {
          if (entry.isIntersecting) {
            visible.value = true;
            await initializeChartData();
            observer?.disconnect();
          }
        },
        {
          rootMargin: "100px",
          threshold: 0.1,
        }
      );

      if (chartRef.value) {
        observer.observe(chartRef.value);
      }
    });

    onBeforeUnmount(() => {
      observer?.disconnect();
    });

    return {
      visible,
      chartRef,
      chartOptions: {
        chart: {
          type: chartType.value,
          height: "32px",
          width: "100%",
          sparkline: { enabled: true },
          animations: {
            enabled: true,
            speed: 800,
            animateGradually: {
              enabled: true,
              delay: 150
            },
            dynamicAnimation: {
              enabled: true,
              speed: 350
            }
          },
          redrawOnWindowResize: false,
          redrawOnParentResize: false
        },
        stroke: {
          width: 2,
          curve: 'smooth'
        },
        fill: {
          gradient: {
            enabled: true,
            opacityFrom: 0.5,
            opacityTo: 0,
          },
        },
        colors: [chartColor.value],
        tooltip: { enabled: false },
        xaxis: {
          type: 'numeric',
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
          tooltip: { enabled: false }
        }
      },
      series,
      chartKey,
      chartType,
      chart,
    };
  },
});
</script>
<style lang="scss" scoped>
.sparkline_container {
  max-height: 60px;
  overflow: hidden;
  border-radius: 0 0 10px 10px;
  min-width: 100%;
  width: 100%;

  .vue-apexcharts {
    min-height: unset !important;
    height: 50px !important;
    width: 100% !important;
  }
}

.UI-of-chart {
  .sparkline_container {
    max-height: 150px;

    .vue-apexcharts {
      height: 100px !important;
    }
  }
}

@media (min-width: 1280px) and (max-width: 1891px) {
  .sparkline_container {
    max-height: 48px;
  }
}
</style>
